import { useEffect, useRef, useState } from 'react';

// Google Identity Services (GIS) web SDK.
const GIS_SRC = 'https://accounts.google.com/gsi/client';

/** Loads the GIS script once and resolves when window.google.accounts.id is ready. */
function loadGis() {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject(new Error('No window'));
    if (window.google?.accounts?.id) return resolve();

    const existing = document.querySelector(`script[src="${GIS_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Google script')));
      if (window.google?.accounts?.id) resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google script'));
    document.head.appendChild(script);
  });
}

/**
 * Renders Google's official "Sign in with Google" button. On success it posts the
 * returned ID token to /api/google-auth together with the selected [role], then
 * redirects to the matching dashboard. Handles both sign-up (first time) and
 * sign-in (returning) — the backend decides which.
 *
 * @param {'job-seeker'|'referrer'} role  currently selected role
 * @param {(msg: string) => void}   onError  surfaces failures to the parent page
 * @param {'signin_with'|'signup_with'|'continue_with'} text  button label variant
 */
export default function GoogleSignInButton({ role, onError, text = 'continue_with' }) {
  const containerRef = useRef(null);
  const roleRef = useRef(role);
  const [busy, setBusy] = useState(false);
  const clientId = process.env.NEXT_PUBLIC_GOOGLE_WEB_CLIENT_ID;

  // Keep the latest role available to the GIS callback (set once at init).
  useEffect(() => { roleRef.current = role; }, [role]);

  useEffect(() => {
    let cancelled = false;

    if (!clientId) {
      onError?.('Google sign-in is not configured yet.');
      return;
    }

    const handleCredential = async (response) => {
      const idToken = response?.credential;
      if (!idToken) {
        onError?.('Google did not return a credential. Please try again.');
        return;
      }
      setBusy(true);
      onError?.('');
      try {
        const res = await fetch('/api/google-auth', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ idToken, role: roleRef.current }),
        });
        const data = await res.json();
        if (res.ok && data.success) {
          const dest = data.user?.role === 'referrer' ? '/referrer-dashboard' : '/job-seeker-dashboard';
          window.location.href = dest;
        } else {
          onError?.(data.message || 'Google sign-in failed. Please try again.');
          setBusy(false);
        }
      } catch (err) {
        onError?.('An unexpected error occurred. Please try again later.');
        setBusy(false);
      }
    };

    loadGis()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredential,
          ux_mode: 'popup',
        });
        // Clear first so React strict-mode double-invoke can't stack two buttons.
        containerRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(containerRef.current, {
          type: 'standard',
          theme: 'filled_blue',
          size: 'large',
          text,
          shape: 'pill',
          logo_alignment: 'center',
          width: 320,
        });
      })
      .catch((err) => onError?.(err.message || 'Could not load Google sign-in.'));

    return () => { cancelled = true; };
    // Initialise once; role is read live via roleRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  return (
    <div className="w-full flex flex-col items-center">
      <div ref={containerRef} className={busy ? 'opacity-50 pointer-events-none' : ''} />
      {busy && <p className="text-blue-200/50 text-xs mt-2">Signing you in…</p>}
    </div>
  );
}
